import { Injectable, inject } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ConfirmationModalComponent, ConfirmationModalData } from './confirmation-modal.component';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ConfirmationModalService {
  private readonly dialog = inject(MatDialog);

  /**
   * Opens a confirmation modal dialog
   * @param data - Configuration for the modal (title, subtitle, message, custom labels)
   * @param config - Optional Material Dialog configuration
   * @returns Promise that resolves to boolean - true if confirmed, false if cancelled
   * @example
   * const confirmed = await confirmationModalService.open({
   *   title: 'Leave Site?',
   *   subtitle: 'You are leaving Heidrick Connect',
   *   message: 'You will be redirected to an external website.',
   *   confirmLabel: 'Continue',
   *   cancelLabel: 'Stay',
   * });
   *
   * if (confirmed) {
   *   window.open('https://example.com', '_blank');
   * }
   */
  async open(
    data: ConfirmationModalData,
    config?: MatDialogConfig<ConfirmationModalData>,
  ): Promise<boolean> {
    const basePanelClass = 'confirmation-modal-panel';
    const extraPanelClass = config?.panelClass;
    const panelClass = extraPanelClass
      ? [basePanelClass, ...(Array.isArray(extraPanelClass) ? extraPanelClass : [extraPanelClass])]
      : basePanelClass;

    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      width: '100%',
      maxWidth: '764px',
      ...config,
      disableClose: true,
      panelClass,
      data,
    });

    try {
      const result = await firstValueFrom(dialogRef.afterClosed());
      return result === true;
    } catch {
      return false;
    }
  }
}
